import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsers } from '../../../actions/users';
import { UserPlus, Edit, Trash2 } from 'lucide-react';
import { UserOutlined } from '@ant-design/icons';
import { Table, Space, Modal, Typography, Empty } from 'antd';
import BatchJobPermissionModal from '../../../components/Modals/Users/BatchJobPermissionModal';

const { Text } = Typography;

const AuthorizedUsers = () => {
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);
  const dispatch = useDispatch();

  const allUsers = useSelector(state => state.users.users || []);
  
  // Filter users to show only those with batch job permissions
  const users = allUsers.filter(user => 
    user.batchJobPermissions && user.batchJobPermissions.length > 0
  );

  // Available permissions for batch jobs
  const permissionOptions = [
    { label: 'View Job Initialisation', value: 'view_batch_jobs_initialisation' },
    { label: 'Trigger Job', value: 'trigger_batch_job' },
    { label: 'View Running Jobs', value: 'view_running_batch_jobs' },
    { label: 'View Scheduled Jobs', value: 'view_scheduled_batch_jobs' },
    { label: 'Schedule Job', value: 'schedule_batch_job' },
    { label: 'Delete Scheduled Job', value: 'delete_scheduled_batch_job' },
    { label: 'Edit Scheduled Job', value: 'edit_scheduled_batch_job' },
  ];

  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  const handleAddUser = () => {
    setUserToEdit(null);
    setModalVisible(true);
  };

  const handleEditUser = (user) => {
    setUserToEdit(user);
    setModalVisible(true);
  };

  const handleDeleteUser = async (userId) => {
    if (!userId) {
      console.error('Cannot delete user: User ID is undefined');
      // message.error('Error: User ID is missing');
      return;
    }

    Modal.confirm({
      title: 'Are you sure you want to remove batch job permissions for this user?',
      content: 'This action cannot be undone.',
      okText: 'Yes, Remove',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          setLoading(true);
          const response = await fetch(`https://cloudify-prod.wizr.in/api/users/${userId}/batch-job-permissions`, {
            method: 'DELETE'
          });
          
          if (!response.ok) {
            throw new Error('Failed to delete permissions');
          }
          
          dispatch(fetchUsers()); // Refresh user list
        } catch (error) {
          console.error('Failed to delete permissions:', error);
          Modal.error({
            title: 'Error',
            content: 'Failed to remove permissions. Please try again.'
          });
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const getPermissionLabel = (permValue) => {
    const permOption = permissionOptions.find(p => p.value === permValue);
    return permOption ? permOption.label : permValue;
  };

  const columns = [
    {
      title: 'User',
      dataIndex: 'name',
      key: 'name',
      width: '30%',
      render: (_, record) => (
        <Space>
          <UserOutlined />
          <div>
            <Text strong>{record.name || 'Unknown'}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: '12px' }}>{record.email || 'No email'}</Text>
          </div>
        </Space>
      ),
    },
    {
      title: 'Permissions',
      dataIndex: 'batchJobPermissions',
      key: 'permissions',
      width: '60%',
      render: (permissions = []) => (
        <div className="flex flex-wrap gap-1">
          {permissions.map(perm => (
            <span 
              key={perm} 
              className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 border border-blue-200"
            >
              {getPermissionLabel(perm)}
            </span>
          ))}
          {permissions.length === 0 && (
            <span className="text-gray-500 italic">No permissions</span>
          )}
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '10%',
      render: (_, record) => (
        <Space>
          <button
            onClick={() => handleEditUser(record)}
            className="inline-flex items-center p-1.5 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50"
            title="Edit permissions"
          >
            <Edit size={16} />
          </button>
          <button
            onClick={() => handleDeleteUser(record.userId)}
            className="inline-flex items-center p-1.5 border border-red-300 rounded-md text-red-600 hover:bg-red-50"
            title="Remove permissions"
          >
            <Trash2 size={16} />
          </button>
        </Space>
      ),
    },
  ];

  const handleCloseModal = () => {
    setModalVisible(false);
    dispatch(fetchUsers()); // Refresh the user list
  };

  return (
    <div className="space-y-6 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
      <div className="flex justify-between items-center pb-4 border-b border-gray-100">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold text-gray-900">Batch Job Permissions</h2>
          <p className="text-gray-500 text-sm">Manage user access to batch job features</p>
        </div>
        <button
          onClick={handleAddUser}
          className="inline-flex items-center text-sm px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-all duration-200 font-medium shadow-md"
        >
          <UserPlus className="h-4 w-4 mr-2" />
          Add User Permission
        </button>
      </div>
      
      <div className="rounded-lg overflow-hidden border border-gray-200">
        <Table
          columns={columns}
          dataSource={users}
          loading={loading}
          rowKey="userId"
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
          }}
          locale={{
            emptyText: (
              <Empty 
                image={Empty.PRESENTED_IMAGE_SIMPLE} 
                description="No users with batch job permissions"
              />
            )
          }}
        />
      </div>
      
      {/* Permission Management Modal */}
      <BatchJobPermissionModal
        isOpen={modalVisible}
        onClose={handleCloseModal}
        userToEdit={userToEdit}
        setUserToEdit={setUserToEdit}
      />
    </div>
  );
};

export default AuthorizedUsers;